import type { FC, ReactNode } from 'react'
import type { GiftPackage } from '@/types'

import { motion } from 'framer-motion'
import { Gift, Star } from 'lucide-react'
import { formatPrice } from '@/lib/format'
import { useTextKeys } from '@/lib/site-content'
import CmsText from '@/components/generals/cms-text'
import { FadeIn } from '@/components/motion/fade-in'

interface AboutProps {
    packages: GiftPackage[]
}

const About: FC<AboutProps> = ({ packages }): ReactNode => {
    const paragraphKeys = useTextKeys('about.paragraphs.')

    return (
        <section id='rreth' className='relative bg-brand-red text-white'>
            <div className='container-page pb-24 md:pb-32'>
                <hr className='mx-auto mb-16 max-w-2xl border-0 border-t border-white/15' />

                <div className='grid grid-cols-1 items-start gap-12 md:grid-cols-2 md:gap-16'>
                    <FadeIn>
                        <div className='relative'>
                            <div className='absolute -inset-6 -z-10 rounded-card bg-brand-yellow/15 blur-2xl' />

                            <div className='rounded-card bg-white/95 p-7 text-neutral-900 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.25)]'>
                                <div className='flex items-center gap-2 text-brand-red'>
                                    <Gift aria-hidden className='h-5 w-5' />
                                    <CmsText as='h3' textKey='about.packagesTitle' className='font-display text-xl font-bold' />
                                </div>

                                <CmsText as='p' textKey='about.packagesSubtitle' className='mt-1 text-sm text-neutral-500' />

                                <ul className='mt-6 space-y-4'>
                                    {packages.map(pkg => (
                                        <PackageRow key={pkg.id} pkg={pkg} />
                                    ))}
                                </ul>

                                <motion.a
                                    href='#porosit'
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className='mt-6 inline-flex w-full items-center justify-center rounded-pill bg-brand-red px-5 py-3 font-display font-semibold text-white shadow-soft'
                                >
                                    <CmsText textKey='hero.ctaPrimary' />
                                </motion.a>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.1}>
                        <CmsText as='h2' textKey='about.title' className='font-display text-3xl font-extrabold md:text-4xl' />

                        <hr className='mt-4 w-16 border-0 border-t-2 border-brand-yellow' />

                        <div className='mt-6 space-y-4 text-white/90'>
                            {paragraphKeys.map(key => (
                                <CmsText key={key} as='p' textKey={key} className='leading-relaxed' />
                            ))}
                        </div>
                    </FadeIn>
                </div>
            </div>

            <svg
                aria-hidden
                viewBox='0 0 1440 80'
                preserveAspectRatio='none'
                className='block h-12 w-full text-brand-cream md:h-20'
            >
                <path
                    fill='currentColor'
                    d='M0,40 C240,80 480,0 720,30 C960,60 1200,90 1440,40 L1440,80 L0,80 Z'
                />
            </svg>
        </section>
    )
}

interface PackageRowProps {
    pkg: GiftPackage
}

const PackageRow: FC<PackageRowProps> = ({ pkg }): ReactNode => {
    const Icon = pkg.featured ? Star : Gift
    const iconColor = pkg.featured ? 'text-brand-yellow' : 'text-brand-red'

    return (
        <li className='flex gap-4 rounded-card bg-brand-cream/50 p-4'>
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-white ${iconColor}`}>
                <Icon aria-hidden className='h-5 w-5' />
            </div>

            <div className='flex-1'>
                <div className='flex items-baseline justify-between gap-3'>
                    <div className='font-display font-bold' data-mdlfy-cms={`packages:${pkg.id}:label`}>
                        {pkg.label}
                    </div>

                    <div className='font-display font-bold text-brand-red'>
                        {formatPrice(pkg.price, pkg.currency)}
                    </div>
                </div>

                <div className='text-sm text-neutral-600' data-mdlfy-cms={`packages:${pkg.id}:tagline`}>
                    {pkg.tagline}
                </div>

                <div className='mt-1 text-xs text-neutral-500' data-mdlfy-cms={`packages:${pkg.id}:includes`}>
                    {pkg.includes}
                </div>
            </div>
        </li>
    )
}

export default About
